/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useState } from 'react';

import { GuardedComponent } from '@/components/GuardedComponent';
import { PageHeader } from '@/components/PageHeader';
import { useMessage } from '@/hooks/message';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { PauseBannersModal } from '@/pages/Jobs/PauseBannersModal';
import { Col } from '@/primitives/Col';
import { Row } from '@/primitives/Row';
import { Switch } from '@/primitives/Switch';
import { Text } from '@/primitives/Text';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import './JobsHeader.css';

export function JobsHeader() {
  const { t } = useMimicTranslation('jobs');
  const [message] = useMessage();
  const jobBannersPaused = useMgmtPlaneStateStore((state) => state.jobBannersPaused);
  const setJobBannersPaused = useMgmtPlaneStateStore((state) => state.setJobBannersPaused);
  const [modalOpen, setModalOpen] = useState(false);

  const handleSwitchChange = (checked: boolean) => {
    if (checked) {
      setModalOpen(true);
    } else {
      setJobBannersPaused(false);
    }
  };

  const handleConfirm = () => {
    setJobBannersPaused(true);
    setModalOpen(false);
    message.success(t('banner.bannersPaused'));
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  return (
    <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ margin: 0 }}>
      <Col xs={24} sm={8} md={12} style={{ padding: 0 }}>
        <PageHeader title={t('listTitle')} level={2} />
      </Col>
      <Col xs={24} sm={16} md={12} xl={10} xxl={6} style={{ padding: 0 }}>
        <div className="jobs-header-pause-banners">
          <GuardedComponent requiredRole="admin">
            <Switch
              checked={jobBannersPaused}
              unCheckedChildren={t('inactive')}
              checkedChildren={t('active')}
              onChange={handleSwitchChange}
              dataTestId="pause-banner-switch"
              style={{
                width: '80px',
                transformOrigin: 'left',
              }}
            />
            <div>
              <Text type="default" style={{ fontWeight: 500 }}>
                {t('pauseBanners')}
              </Text>
            </div>
          </GuardedComponent>
        </div>
      </Col>
      <PauseBannersModal open={modalOpen} onConfirm={handleConfirm} onCancel={handleCancel} />
    </Row>
  );
}
